import { SkeletonText } from '@carbon/react';
import { DECIMAL_FORMATTER_TWO_FRACTIONS } from 'lib/ui';
import React, { useMemo } from 'react';
import { formatNumber } from 'utils/number';
import { formatVolumeWhole } from 'components/well-page-content/utils';
import styles from './eur-table.module.scss';
import classNames from 'classnames';

interface AggregationMetrics {
  oilEur?: number | null;
  gasEur?: number | null;
  waterEur?: number | null;
  recoveredOil?: number | null;
  recoveredGas?: number | null;
  recoveredWater?: number | null;
  remainingOil?: number | null;
  remainingGas?: number | null;
  remainingWater?: number | null;
  gasOilRatioTotal?: number | null;
  waterCutTotal?: number | null;
  waterGasRatioTotal?: number | null;
  waterOilRatioTotal?: number | null;
  oilGasRatioTotal?: number | null;
}

/** One set of figures, shown as a column under its own heading. */
export interface EurColumn {
  label: string;
  metrics: AggregationMetrics | null;
}

interface EurTableProps {
  volumeMetricData?: AggregationMetrics | null;
  isLoading?: boolean;
  typeCurveMetricData?: AggregationMetrics | null;
  /**
   * The columns to show. Given instead of the two metric props when there is a
   * column per selected forecast rather than a forecast and its type curve.
   */
  columns?: EurColumn[];
}

interface RatioConfig {
  label: string;
  key: keyof AggregationMetrics;
  colorClass: string;
  unit: string;
}

interface StreamConfig {
  title: string;
  colorClass: string;
  unit: string;
  eur: keyof AggregationMetrics;
  recovered: keyof AggregationMetrics;
  remaining: keyof AggregationMetrics;
  ratios: RatioConfig[];
}

/** The three streams, in the order the table shows them. */
const STREAMS: StreamConfig[] = [
  {
    title: 'OIL',
    colorClass: styles.oilColor,
    unit: 'bbl',
    eur: 'oilEur',
    recovered: 'recoveredOil',
    remaining: 'remainingOil',
    ratios: [
      {
        label: 'GOR',
        key: 'gasOilRatioTotal',
        colorClass: styles.gorColor,
        unit: 'mcf/bbl',
      },
      {
        label: 'WOR',
        key: 'waterOilRatioTotal',
        colorClass: styles.worColor,
        unit: 'bbl/bbl',
      },
    ],
  },
  {
    title: 'GAS',
    colorClass: styles.gasColor,
    unit: 'mcf',
    eur: 'gasEur',
    recovered: 'recoveredGas',
    remaining: 'remainingGas',
    ratios: [
      {
        label: 'CGR',
        key: 'oilGasRatioTotal',
        colorClass: styles.cgrColor,
        unit: 'bbl/Mmcf',
      },
      {
        label: 'WGR',
        key: 'waterGasRatioTotal',
        colorClass: styles.wgrColor,
        unit: 'bbl/Mmcf',
      },
    ],
  },
  {
    title: 'WATER',
    colorClass: styles.waterColor,
    unit: 'bbl',
    eur: 'waterEur',
    recovered: 'recoveredWater',
    remaining: 'remainingWater',
    ratios: [
      {
        label: 'Watercut',
        key: 'waterCutTotal',
        colorClass: styles.waterCutColor,
        unit: '%',
      },
    ],
  },
];

/**
 * The grid the headers and each row share: a label column, then one column per
 * set of figures. Set here rather than in the stylesheet because the number of
 * columns is only known at render.
 *
 * @param {number} count - How many columns of figures there are.
 * @param {number} labelWidth - Width of the leading label column, in pixels.
 * @return {React.CSSProperties} The grid template.
 */
const gridFor = (count: number, labelWidth: number): React.CSSProperties => ({
  gridTemplateColumns: `${labelWidth}px repeat(${count}, minmax(110px, 1fr))`,
});

const renderValue = (
  value: number | undefined | null,
  colorClass: string,
  unit: string,
  formatter: (val: number) => string = DECIMAL_FORMATTER_TWO_FRACTIONS.format
) => {
  if (value === undefined || value === null) {
    return <span className={styles.unit}>-- {unit}</span>;
  }

  return (
    <>
      <span className={colorClass}>{value === 0 ? '0' : formatter(value)}</span>
      <span
        className={classNames(
          styles.unit,
          unit === '%' && styles.waterPercentage
        )}
      >
        {' '}
        {unit}
      </span>
    </>
  );
};

const StreamSection: React.FC<{
  stream: StreamConfig;
  columns: EurColumn[];
}> = ({ stream, columns }) => {
  const row = (
    label: string,
    key: keyof AggregationMetrics,
    colorClass: string,
    unit: string,
    formatter?: (val: number) => string
  ) => (
    <div
      key={label}
      className={styles.forecastRow}
      style={gridFor(columns.length, 120)}
    >
      <span className={styles.rowLabel}>{label}</span>
      {columns.map((column) => (
        <span key={column.label} className={styles.rowValue}>
          {renderValue(column.metrics?.[key], colorClass, unit, formatter)}
        </span>
      ))}
    </div>
  );

  return (
    <div
      className={classNames(styles.forecastSection, {
        [styles.lastColumn]: stream.title === 'WATER',
      })}
    >
      <h4 className={styles.forecastTitle}>{stream.title}</h4>
      <div
        className={styles.columnHeaders}
        style={gridFor(columns.length, 100)}
      >
        {columns.map((column) => (
          <span key={column.label} className={styles.columnHeader}>
            {column.label}
          </span>
        ))}
      </div>

      {row(
        'Recovered',
        stream.recovered,
        stream.colorClass,
        stream.unit,
        formatVolumeWhole
      )}
      {row(
        'Remaining',
        stream.remaining,
        stream.colorClass,
        stream.unit,
        formatVolumeWhole
      )}
      {row(
        'EUR',
        stream.eur,
        stream.colorClass,
        stream.unit,
        formatVolumeWhole
      )}

      {stream.ratios.map((ratio) =>
        row(
          ratio.label,
          ratio.key,
          ratio.colorClass,
          ratio.unit,
          ratio.unit === '%'
            ? (val: number) => formatNumber(val, 1, 1, 'decimal', true)
            : undefined
        )
      )}

      {stream.title === 'WATER' && <div style={{ height: '21px' }} />}
    </div>
  );
};

export const EurTable: React.FC<EurTableProps> = ({
  volumeMetricData,
  typeCurveMetricData,
  isLoading = false,
  columns,
}) => {
  const tableColumns = useMemo((): EurColumn[] => {
    if (columns) return columns;

    return [
      { label: 'DCA Forecast', metrics: volumeMetricData ?? null },
      ...(typeCurveMetricData
        ? [{ label: 'Scaled Type Curve', metrics: typeCurveMetricData }]
        : []),
    ];
  }, [columns, volumeMetricData, typeCurveMetricData]);

  if (isLoading) return <EurTableSkeleton />;

  if (columns ? tableColumns.length === 0 : !volumeMetricData) return null;

  return (
    <div className={styles.bannerContainer}>
      <div className={styles.forecastTable}>
        {STREAMS.map((stream) => (
          <StreamSection
            key={stream.title}
            stream={stream}
            columns={tableColumns}
          />
        ))}
      </div>
    </div>
  );
};

const EurTableSkeleton: React.FC = () => {
  return (
    <div className={styles.bannerContainer}>
      <div className={styles.forecastTable}>
        {STREAMS.map((stream) => (
          <div key={stream.title} className={styles.forecastSection}>
            <SkeletonText heading width="200px" />
            <div className={styles.forecastFields}>
              <SkeletonText width="100px" />
              <SkeletonText width="100px" />
              <SkeletonText width="100px" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
